import { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils.ts';
import { useAuth } from '@/lib/auth/AuthProvider';
import { UserRole } from '@/types/auth.ts';
import { getTenantItem, setTenantItem, migrateLegacyKey } from '@/lib/tenantStorage';
import { useFeature } from '@/lib/features/FeatureProvider';
import { useIsModuleVisible } from '@/hooks/useModuleVisibility';
import {
  LayoutDashboard,
  DollarSign,
  FolderOpen,
  FileText,
  FileCheck,
  Files,
  Users,
  ChartBar,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  AlertCircle,
  FilePlus2,
  MapPin,
  Send,
  Clock,
  AlertTriangle,
  List,
  Layers,
  Globe,
  Palette,
  ExternalLink,
} from 'lucide-react';
import { Button } from '@/components/ui/button.tsx';
import { useUserViews } from '@/hooks/useUserViews';
import { APP_VERSION } from '@/lib/version/versionCheck';

interface NavItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  badge?: number;
  moduleId?: string;
  children?: NavItem[];
}

const navigation: NavItem[] = [
  {
    title: 'Dashboard',
    href: '/dashboard',
    icon: LayoutDashboard
  },
  {
    title: 'Projects',
    href: '/projects',
    icon: FolderOpen,
    children: [
      {
        title: 'All Projects',
        href: '/projects',
        icon: FolderOpen
      },
      {
        title: 'Proposals',
        href: '/projects?view=proposals',
        icon: FilePlus2,
        moduleId: 'proposals',
      }
    ]
  },
  {
    title: 'Work',
    href: '/deliverables',
    icon: Layers,
    children: [
      {
        title: 'Deliverables',
        href: '/deliverables',
        icon: Layers,
        moduleId: 'cross_project_deliverables'
      },
      {
        title: 'Billable Items',
        href: '/billable',
        icon: CircleDollarSign,
        moduleId: 'cross_project_billable'
      },
      {
        title: 'Permits & Submissions',
        href: '/permits',
        icon: FileCheck,
        moduleId: 'cross_project_permits'
      }
    ]
  },
  {
    title: 'Billing',
    href: '/invoices',
    icon: FileText,
    children: [
      {
        title: 'Invoices',
        href: '/invoices',
        icon: FileText
      },
      {
        title: 'Reports',
        href: '/reports',
        icon: ChartBar
      }
    ]
  },
  {
    title: 'Contacts',
    href: '/organizations',
    icon: Users
  },
  {
    title: 'Files',
    href: '/files',
    icon: Files,
    moduleId: 'cross_project_files'
  },
  // Top-level RFIs / Submittals / Supplemental Instructions nav links removed
  // 2026-05-09 — those pages were placeholders ("managed within each project")
  // and the real workflows live under /projects/:projectId/*. Component files
  // and routes are kept (with redirects in App.tsx) for the eventual
  // cross-project view. See roadmap: rfi-submittals-e2e.
];

interface SideNavProps {
  mobileOpen?: boolean;
  onMobileClose?: () => void;
}

export function SideNav({ mobileOpen, onMobileClose }: SideNavProps) {
  const location = useLocation();
  const { user } = useAuth();
  const tenantId = user?.tenant_id ?? '';
  // The Stakeholder Portal / Coordination nav gates that lived here (the
  // per-user `stakeholder_portal` capability + `is_super_admin`) moved to the
  // header YourSubmissionsBell (SPEC-stakeholder-portal-presentation-redesign
  // R1 AC1 / R2 AC2) along with the entry points themselves.
  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<string[]>([]);
  const isModuleVisible = useIsModuleVisible;

  // Load collapse state from tenant-scoped localStorage
  useEffect(() => {
    if (!tenantId) return;
    migrateLegacyKey('sideNavCollapsed', tenantId);
    const saved = getTenantItem('sideNavCollapsed', tenantId);
    if (saved) {
      setCollapsed(JSON.parse(saved));
    }
  }, [tenantId]);

  // Save collapse state to tenant-scoped localStorage
  useEffect(() => {
    if (tenantId) {
      setTenantItem('sideNavCollapsed', JSON.stringify(collapsed), tenantId);
    }
  }, [collapsed, tenantId]);

  const toggleExpanded = (title: string) => {
    setExpandedItems(prev =>
      prev.includes(title)
        ? prev.filter(item => item !== title)
        : [...prev, title]
    );
  };

  const isActive = (href: string) => {
    if (href === '/dashboard' && location.pathname === '/') return true;

    // 9.7d: Handle hrefs with query parameters (e.g., /projects?view=proposals)
    if (href.includes('?')) {
      const [hrefPath, hrefQuery] = href.split('?');
      const hrefParams = new URLSearchParams(hrefQuery);
      const currentParams = new URLSearchParams(location.search);

      // Check path matches AND all href query params exist in current URL
      if (location.pathname !== hrefPath) return false;
      for (const [key, value] of hrefParams.entries()) {
        if (currentParams.get(key) !== value) return false;
      }
      return true;
    }

    // For paths without query params, use exact match for better child item handling
    return location.pathname === href ||
           (location.pathname.startsWith(href + '/') && href !== '/');
  };

  // Auto-expand parent groups when a child route is active
  useEffect(() => {
    const activeParents: string[] = [];
    for (const item of navigation) {
      if (item.children) {
        const childActive = item.children.some(child => isActive(child.href));
        if (childActive && !expandedItems.includes(item.title)) {
          activeParents.push(item.title);
        }
      }
    }
    if (activeParents.length > 0) {
      setExpandedItems(prev => [...new Set([...prev, ...activeParents])]);
    }
  }, [location.pathname, location.search]);

  // Close mobile menu on navigation
  useEffect(() => {
    if (mobileOpen && onMobileClose) {
      onMobileClose();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  const sidebarContent = (
    <div
      className={cn(
        'relative flex h-screen flex-col border-r bg-background transition-all duration-300',
        collapsed ? 'w-16' : 'w-64'
      )}
    >
      {/* Header */}
      <div className={cn(
        'flex h-16 items-center border-b',
        collapsed ? 'justify-center px-2' : 'justify-between px-4'
      )}>
        {collapsed ? (
          <button
            onClick={() => setCollapsed(false)}
            aria-label="Expand sidebar"
            className="flex items-center justify-center gap-1 rounded-md p-1 hover:bg-muted transition-colors cursor-pointer"
          >
            <img
              src="/keystone-icon.png"
              alt="Keystone"
              className="h-8 w-8 flex-shrink-0"
            />
            <ChevronRight size={14} className="text-muted-foreground" />
          </button>
        ) : (
          <>
            <div className="flex items-center min-w-0 flex-1">
              <img
                src="/keystone-logo.png"
                alt="Keystone"
                className="h-10 w-auto flex-shrink-0"
              />
            </div>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setCollapsed(!collapsed)}
              aria-label="Collapse sidebar"
              className="ml-auto"
            >
              <ChevronLeft size={18} />
            </Button>
          </>
        )}
      </div>

      {/* Navigation Items */}
      <nav className={cn('flex-1 space-y-1 overflow-y-auto p-2', collapsed ? 'overflow-x-visible' : 'overflow-x-hidden')}>
        {/* EXTERNAL users only see their views section — internal nav is hidden */}
        {user?.role === UserRole.EXTERNAL ? null : navigation
          .filter(item => !item.moduleId || isModuleVisible(item.moduleId))
          .map((item) => {
          // Filter children by module visibility too
          const visibleChildren = item.children?.filter(
            c => !c.moduleId || isModuleVisible(c.moduleId)
          );
          const navItem = visibleChildren !== undefined ? { ...item, children: visibleChildren } : item;
          const Icon = navItem.icon;
          const isExpanded = expandedItems.includes(navItem.title);
          const hasChildren = navItem.children && navItem.children.length > 0;
          const isItemActive = isActive(navItem.href);

          return (
            <div key={navItem.title}>
              {/* Main Item */}
              <div className="relative group">
                <Link
                  to={navItem.href}
                  className={cn(
                    'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                    isItemActive
                      ? 'bg-primary/10 text-primary'
                      : 'hover:bg-muted hover:text-accent-foreground',
                    collapsed && 'justify-center'
                  )}
                >
                  <Icon className={cn('flex-shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
                  {!collapsed && (
                    <>
                      <span className="flex-1">{navItem.title}</span>
                      {navItem.badge && (
                        <span className="ml-auto rounded-full bg-primary/20 px-2 py-0.5 text-xs">
                          {navItem.badge}
                        </span>
                      )}
                    </>
                  )}
                </Link>

                {/* Expand/Collapse button for items with children */}
                {!collapsed && hasChildren && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-2 top-1/2 -translate-y-1/2 h-6 w-6 p-0 hover:bg-muted/50"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      toggleExpanded(navItem.title);
                    }}
                  >
                    <ChevronRight
                      className={cn(
                        'h-3 w-3 transition-transform',
                        isExpanded && 'rotate-90'
                      )}
                    />
                  </Button>
                )}

                {/* Tooltip for collapsed state */}
                {collapsed && (
                  <div className="pointer-events-none absolute left-14 top-1/2 z-50 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100">
                    <div className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md">
                      {navItem.title}
                    </div>
                  </div>
                )}
              </div>

              {/* Children Items */}
              {!collapsed && hasChildren && isExpanded && (
                <div className="ml-4 mt-1 space-y-1 border-l pl-3">
                  {navItem.children?.map((child) => {
                    const ChildIcon = child.icon;
                    return (
                      <Link
                        key={child.title}
                        to={child.href}
                        className={cn(
                          'flex items-center gap-3 rounded-md px-3 py-1.5 text-sm transition-colors',
                          isActive(child.href)
                            ? 'text-primary'
                            : 'text-muted-foreground hover:text-foreground'
                        )}
                      >
                        <ChildIcon className="h-3 w-3" />
                        <span>{child.title}</span>
                      </Link>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}

        {/* Stakeholder Portal + Internal Coordination View left-nav entries were
            RETIRED here (SPEC-stakeholder-portal-presentation-redesign-2026-07-20,
            R1 AC1). Both surfaces now live behind the header "your submissions"
            icon (YourSubmissionsBell, mounted in DashboardLayout next to Report
            Feedback) instead of a dedicated left-nav slot. */}

        {/* My Views - Phase 10a.9 (internal users only) */}
        {user?.role !== UserRole.EXTERNAL && (
          <UserViewsSection collapsed={collapsed} isActive={isActive} />
        )}

        {/* External portal link — shown only to EXTERNAL users */}
        {user?.role === UserRole.EXTERNAL && (
          <Link
            to="/my-views"
            className={cn(
              'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
              isActive('/my-views')
                ? 'bg-primary/10 text-primary'
                : 'hover:bg-muted hover:text-accent-foreground',
              collapsed && 'justify-center'
            )}
          >
            <LayoutDashboard className={cn('flex-shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
            {!collapsed && <span className="flex-1">My Views</span>}
          </Link>
        )}

        {/* planTEK feature-gated nav items — only visible to internal users with flags enabled */}
        {user?.role !== UserRole.EXTERNAL && (
          <PlanTEKNavSection collapsed={collapsed} isActive={isActive} />
        )}
      </nav>

      {/* Footer — visible version badge (SPEC-stakeholder-portal R5 AC5) so a
          stakeholder can see the build they're running and verify "fixed in the
          version you're running". Sourced from APP_VERSION (VITE_APP_VERSION). */}
      <div className="border-t p-4">
        {collapsed ? (
          <div
            className="text-center text-[10px] font-mono text-muted-foreground truncate"
            title={`Version ${APP_VERSION}`}
            data-testid="sidenav-version-badge"
          >
            {APP_VERSION}
          </div>
        ) : (
          <div className="text-xs text-muted-foreground">
            <div>Keystone</div>
            <div
              className="mt-1 font-mono text-[11px]"
              data-testid="sidenav-version-badge"
            >
              Version {APP_VERSION}
            </div>
          </div>
        )}
      </div>
    </div>
  );

  // Desktop: render inline (hidden on mobile)
  // Mobile: render as overlay when mobileOpen
  return (
    <>
      {/* Desktop sidebar */}
      <div className="hidden lg:block">
        {sidebarContent}
      </div>

      {/* Mobile overlay */}
      {mobileOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={onMobileClose} />
          <div className="relative w-64 h-full">
            {sidebarContent}
          </div>
        </div>
      )}
    </>
  );
}


// Icon mapping for view icons (lucide-react icon name → component)
const VIEW_ICON_MAP: Record<string, React.ComponentType<{ className?: string }>> = {
  send: Send,
  clock: Clock,
  'alert-triangle': AlertTriangle,
  list: List,
  'file-text': FileText,
  'dollar-sign': DollarSign,
  layers: Layers,
};

function UserViewsSection({
  collapsed,
  isActive,
}: {
  collapsed: boolean;
  isActive: (href: string) => boolean;
}) {
  const { data } = useUserViews({ pinned_only: true });
  const views = data?.views || [];

  if (views.length === 0) return null;

  return (
    <>
      {/* Divider */}
      {collapsed ? (
        <div className="border-t my-2" />
      ) : (
        <div className="px-3 py-2 mt-2 border-t pt-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            My Views
          </span>
        </div>
      )}

      {/* View items */}
      {views.map((view) => {
        const Icon = VIEW_ICON_MAP[view.icon] || List;
        const href = `/views/${view.id}`;
        const active = isActive(href);

        return (
          <div key={view.id} className="relative group">
            <Link
              to={href}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-muted hover:text-accent-foreground',
                collapsed && 'justify-center'
              )}
            >
              <Icon className={cn('flex-shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
              {!collapsed && <span className="flex-1">{view.name}</span>}
            </Link>

            {/* Tooltip for collapsed state */}
            {collapsed && (
              <div className="pointer-events-none absolute left-14 top-1/2 z-50 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md">
                  {view.name}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}

// ---------------------------------------------------------------------------
// planTEK-only nav section — hidden from Banks Architecture tenants
// ---------------------------------------------------------------------------

interface PlanTEKItem {
  title: string;
  href: string;
  icon: React.ComponentType<{ className?: string }>;
  feature: string;
}

const PLANTEK_NAV_ITEMS: PlanTEKItem[] = [
  { title: 'Portal Management', href: '/portal-management', icon: Globe, feature: 'portal_management' },
  { title: 'Theme Settings', href: '/theme-settings', icon: Palette, feature: 'theming' },
  { title: 'ClickUp', href: '/clickup', icon: ExternalLink, feature: 'clickup_integration' },
];

function PlanTEKNavSection({
  collapsed,
  isActive,
}: {
  collapsed: boolean;
  isActive: (href: string) => boolean;
}) {
  const hasPortal = useFeature('portal_management');
  const hasTheming = useFeature('theming');
  const hasClickUp = useFeature('clickup_integration');
  const isPortalModuleVisible = useIsModuleVisible('portal');

  const featureMap: Record<string, boolean> = {
    portal_management: hasPortal && isPortalModuleVisible,
    theming: hasTheming,
    clickup_integration: hasClickUp,
  };

  const visibleItems = PLANTEK_NAV_ITEMS.filter((item) => featureMap[item.feature]);

  if (visibleItems.length === 0) return null;

  return (
    <>
      {/* Divider + section label */}
      {collapsed ? (
        <div className="border-t my-2" />
      ) : (
        <div className="px-3 py-2 mt-2 border-t pt-3">
          <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Client Portal
          </span>
        </div>
      )}

      {visibleItems.map((item) => {
        const Icon = item.icon;
        const active = isActive(item.href);
        return (
          <div key={item.title} className="relative group">
            <Link
              to={item.href}
              className={cn(
                'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                active
                  ? 'bg-primary/10 text-primary'
                  : 'hover:bg-muted hover:text-accent-foreground',
                collapsed && 'justify-center'
              )}
            >
              <Icon className={cn('flex-shrink-0', collapsed ? 'h-5 w-5' : 'h-4 w-4')} />
              {!collapsed && <span className="flex-1">{item.title}</span>}
            </Link>

            {collapsed && (
              <div className="pointer-events-none absolute left-14 top-1/2 z-50 -translate-y-1/2 opacity-0 transition-opacity group-hover:opacity-100">
                <div className="rounded-md bg-popover px-3 py-1.5 text-sm text-popover-foreground shadow-md">
                  {item.title}
                </div>
              </div>
            )}
          </div>
        );
      })}
    </>
  );
}